import { DB_FIELD_MAX_LENGTH } from "@selector/shared/settings";
import type { FC } from "react";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { formValueSelector, type InjectedFormProps, reduxForm, SubmissionError } from "redux-form";
import { CalloutIntent, Dialog } from "widgets";
import { type ControlledDialog, useControlledDialog } from "widgets/hooks";

import { useError } from "@/app-utils/graphql/useError.ts";
import type { InputModifyPlaybookEventInput } from "@/graphql.ts";
import { modifyEvent } from "@/redux/modules/event.ts";
import type { IAppState } from "@/redux/reducer.ts";
import type { ReduxDispatch } from "@/redux/redux-utils.ts";
import InputField, { InputReduxFormField } from "@/ui-modules/core/redux-form/InputField.tsx";
import { useGetPlaybookEventEditableFieldsQuery } from "@/ui-modules/playbooks/events/graphql/event-queries.gql.ts";

interface PlaybookEventEditDialogProps extends ControlledDialog {
  eventId?: number;
  onSave: () => void;
}

enum PlaybookEventEditDialogFormNames {
  FORM_NAME = "editPlaybookEvent",
  NAME = "name",
  DESCRIPTION = "description",
}

const PlaybookEventEditDialogForm: FC<PlaybookEventEditDialogProps & InjectedFormProps<any, any>> = (props) => {
  const { dialogProps, underlayProps, dialogRef } = useControlledDialog(props.onSave);
  const { data, error } = useGetPlaybookEventEditableFieldsQuery({
    variables: { eventId: props.eventId },
  });
  void useError(error);
  const event = data?.playbook?.event;

  const dispatch = useDispatch<ReduxDispatch>();
  const state = useSelector<IAppState, IAppState>((state) => state);

  function handleSubmitData(): Promise<any> {
    const valueSelector = formValueSelector(PlaybookEventEditDialogFormNames.FORM_NAME);
    const payload: InputModifyPlaybookEventInput = {
      eventId: event.id,
      alias: valueSelector(state, PlaybookEventEditDialogFormNames.NAME),
      note: valueSelector(state, PlaybookEventEditDialogFormNames.DESCRIPTION),
    };

    return dispatch(modifyEvent(payload))
      .then(() => {
        props.onSave();
      })
      .catch((rejected: Error) => {
        throw new SubmissionError({ _error: rejected.message });
      });
  }

  useEffect(() => {
    if (event) {
      props.initialize({
        [PlaybookEventEditDialogFormNames.NAME]: event.alias,
        [PlaybookEventEditDialogFormNames.DESCRIPTION]: event.note,
      });
    }
  }, [event]);

  return (
    <Dialog
      ref={dialogRef}
      domProps={dialogProps}
      underlayProps={underlayProps}
      closeFn={() => props.onCancel?.()}
      title={"Edit Playbook Event"}
      errorCallout={
        props.error
          ? { message: props.error }
          : props.warning && { message: props.warning, intent: CalloutIntent.Warning }
      }
      mainButton={{
        label: "Save",
        onPress: props.handleSubmit(handleSubmitData),
      }}
      width={480}
      data-testid={`playbook-edit-dialog`}
    >
      <InputReduxFormField
        componentClass="input"
        name={PlaybookEventEditDialogFormNames.NAME}
        label="Name"
        placeholder="Optional name"
        maxLength={DB_FIELD_MAX_LENGTH.EVENT.ALIAS}
        component={InputField}
        horizontalLayout={false}
      />
      <InputReduxFormField
        componentClass="textarea"
        name={PlaybookEventEditDialogFormNames.DESCRIPTION}
        label="Description"
        placeholder="Optional description"
        maxLength={DB_FIELD_MAX_LENGTH.EVENT.NOTE}
        component={InputField}
        horizontalLayout={false}
        rows={3}
      />
    </Dialog>
  );
};

export const PlaybookEventEditDialog = reduxForm<unknown, PlaybookEventEditDialogProps>({
  form: PlaybookEventEditDialogFormNames.FORM_NAME,
})(PlaybookEventEditDialogForm);
